/**
 * What rista-library needs to price a sale, from a snapshot of the store: web v1's
 * `getParamsForSaleCalculate`, `getAllDiscounts` and `getSaleItems`
 * (`helpers/saleHelpers/index.desktop.ts`), taken as the ONE body on both platforms
 * (NEW_ORDER_V2_PLAN.md rule 9, §2.7). Mobile's differed: it passed `transactionEnd: []`, no
 * `itemsData` and no `optionSetsData`, and cloned nothing (ledger L49). Pure: the state is an
 * argument, so a test needs no store.
 */
import moment from "moment";
import "moment-timezone";
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";

/** core's tsconfig carries no DOM lib; the global exists on web and on mobile (Hermes polyfill) */
declare function structuredClone<T>(value: T): T;

/** the slices pricing reads */
export interface PricingState {
  catalogStore?: any;
  itemsStore?: any;
  businessStore?: any;
  discountStore?: any;
}

/** the catalogue's item record behind each line, in order */
export function catalogItemsFor(saleItems: any[] = [], state: PricingState): any[] {
  const itemData = state?.itemsStore?.itemsData || {};
  return saleItems.map((line) => itemData?.[line?.itemId]);
}

/** every discount the bill may take: the catalogue's, plus an applied coupon expanded by rista-library */
export function billableDiscounts(state: PricingState): any[] {
  const catalog = state.catalogStore?.discounts;
  const coupon = state.discountStore?.discountInfo;
  const fromCatalog = catalog && Object.values(catalog).length ? Object.values(catalog) : [];
  const fromCoupon = coupon?.discounts?.length
    ? saleutil.couponToDiscounts({ coupons: [coupon] }) || []
    : [];
  return [...fromCatalog, ...fromCoupon];
}

/**
 * The parameter bundle for `calculateForSale`.
 *
 * ⚠️ `charges` MUST be cloned: the library WRITES to it (`charge.scope`). The rest of the catalogue
 * is read-only inside the library; the discounts, items and maps are cloned as web clones them.
 */
export function pricingParams(sale: any, state: PricingState): Record<string, unknown> {
  const catalog = state.catalogStore || {};
  const business = state.businessStore?.businessData || {};
  const zone = business?.timeZone?.zoneId;
  return {
    charges: catalog.charges ? structuredClone(Object.values(catalog.charges)) : [],
    bizBrands: catalog.bizBrandsData || [],
    billRoundingMode: business.roundingMode || "",
    branchTime: zone
      ? moment().tz(zone).format("YYYY-MM-DDTHH:MM:SS")
      : moment().format("YYYY-MM-DDTHH:MM:SS"),
    brands: catalog.brandsData || [],
    categories: catalog.categoryData ? Object.values(catalog.categoryData) : [],
    categoryGroups: catalog.categoryGroup || [],
    channelDefs: catalog.channelDef || [],
    channelvos: catalog.channelvos || [],
    discounts: structuredClone(billableDiscounts(state)),
    items: structuredClone(catalogItemsFor(sale?.saleItems, state)),
    itemsData: structuredClone(state.itemsStore?.itemsData || {}),
    optionSetsData: structuredClone(state.itemsStore?.optionMapData || {}),
    taxArea: catalog.taxArea || [],
    taxTypes: catalog.taxTypes || [],
    transactionEnd: business.transactionEnd,
    roundOnlyCashPaymentMode: business.enableRoundingCashOnly || false,
  };
}
